import { Box, Chip, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import LogoLoader from "../../common/LogoLoader";
import { useAuth } from "../../../contexts/AuthContext";
import { useSupplyFlow } from "../../../hooks/queries/useSupplyFlow";
import { SupplyFlow } from "../../../types/analytics";
import { ExportFacts } from "../../../utils/export/ExportFrame";
import { useCapturing } from "../../../utils/export/ExportStage";
import { rememberHome, rememberedHome } from "../../ValueChain/homeMemory";
import { AnalyticsPaneProps } from "../analyticsPanes";
import {
  ANALYTICS_VIEW_LABELS,
  SupplyFlowSettings,
  selectionOf,
  toSupplyFlowParams,
} from "../analyticsState";
import SupplyFlowChart, {
  BAND_OPACITY,
  FlowSelection,
} from "./SupplyFlowChart";
import SupplyFlowControls from "./SupplyFlowControls";
import SupplyFlowPanel from "./SupplyFlowPanel";
import { layoutSupplyFlow } from "./supplyFlowLayout";
import { MORE_MAKERS_ID, counted, indexNodes } from "./supplyFlowModel";

// The floors in force and the makers left out, as a caption
export const floorsCaption = (flow: SupplyFlow): string =>
  [
    `a node needs ${counted(flow.floors.min_support, "product")} to count firmly`,
    `at most ${flow.floors.top_n} makers drawn`,
    ...(flow.omitted_makers > 0
      ? [`${counted(flow.omitted_makers, "maker")} not drawn`]
      : []),
    ...(flow.thin_scope
      ? [`the scope holds under ${counted(flow.floors.min_scope, "product")}: every number is a hint`]
      : []),
  ].join(" · ");

// The sample before a legend entry: a fill, or a dashed outline
const Swatch: React.FC<{ fill?: string; dashed?: string }> = ({ fill, dashed }) => (
  <Box
    component="span"
    aria-hidden
    sx={{
      display: "inline-block",
      width: 22,
      height: 10,
      mr: "5px",
      verticalAlign: "middle",
      borderRadius: "2px",
      boxSizing: "border-box",
      backgroundColor: fill ?? "transparent",
      border: dashed ? `1.5px dashed ${dashed}` : "none",
    }}
  />
);

// The legend states only what the drawn flow holds: an entry whose mark is
// nowhere in the drawing is left out
const Legend: React.FC<{ flow: SupplyFlow; showNeeds: boolean }> = ({
  flow,
  showNeeds,
}) => {
  const theme = useTheme();
  const { chart } = theme.palette;
  const nodes = flow.columns.flatMap((column) => column.nodes);
  const has = (bucket: string) => nodes.some((node) => node.bucket === bucket);
  const entries: [string, React.ReactNode][] = [
    [
      "band",
      <>
        <Swatch fill={alpha(theme.palette.accent.main, 0.35)} />
        band = products on record, width by count
      </>,
    ],
  ];
  if (showNeeds && flow.bands.some((band) => band.need)) {
    entries.push([
      "need",
      <>
        <Swatch dashed={chart.need} />
        dashed = need stated, nothing of the kind on record
      </>,
    ]);
  }
  if (has("domestic")) {
    entries.push([
      "home",
      <>
        <Swatch fill={alpha(chart.home, 0.4)} />
        home jurisdiction
      </>,
    ]);
  }
  if (has("trusted")) {
    entries.push([
      "trusted",
      <>
        <Swatch fill={alpha(chart.home, BAND_OPACITY.trusted + 0.05)} />
        trusted country
      </>,
    ]);
  }
  if (has("other")) {
    entries.push([
      "other",
      <>
        <Swatch fill={alpha(chart.muted, 0.35)} />
        other country on record
      </>,
    ]);
  }
  const dashedNodes = [
    ...(nodes.some((node) => node.thin) ? ["under the floor"] : []),
    ...(has("unknown") ? ["country unknown"] : []),
    ...(nodes.some((node) => node.id === MORE_MAKERS_ID) ? ["makers not drawn"] : []),
  ];
  if (dashedNodes.length > 0) {
    entries.push(["dashed", `dashed or faint = ${dashedNodes.join(", ")}`]);
  }
  return (
    <Box
      role="list"
      aria-label="Legend"
      sx={{
        display: "flex",
        columnGap: "18px",
        rowGap: 0.5,
        flexWrap: "wrap",
        alignItems: "center",
        fontSize: 12,
        color: "text.secondary",
      }}
    >
      {entries.map(([key, entry]) => (
        <span key={key} role="listitem">
          {entry}
        </span>
      ))}
    </Box>
  );
};

// The supply flow: the hosts that carry a kind of part, the kinds, their
// makers and the countries the makers are at home in, as four columns of
// nodes joined by bands as wide as the products they carry.
const SupplyFlowView: React.FC<AnalyticsPaneProps> = ({
  state,
  onChange,
  onOpenEntity,
}) => {
  const settings = state.settings["supply-flow"];
  const userId = useAuth().user?.id ?? null;
  // The home the address names, else the one remembered for the user
  const home = settings.home ?? rememberedHome(userId);
  const params = useMemo(
    () => toSupplyFlowParams(state, rememberedHome(userId)),
    [state, userId],
  );
  const read = useSupplyFlow(params);
  const flow: SupplyFlow | null = read.data ?? null;

  const [selected, setSelected] = useState<FlowSelection | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  // Whether the need bands are drawn: a matter of the drawing, not the read
  const [showNeeds, setShowNeeds] = useState(true);

  const noKinds =
    flow !== null &&
    (flow.columns.find((column) => column.id === "kinds")?.nodes.length ?? 0) === 0;
  const drawn = flow !== null && !noKinds;

  // The size of the drawing: the canvas's, measured and followed; where
  // there is none to measure the layout takes its own. A pixel is held
  // back, so that a rounded size never calls up a scrollbar.
  const container = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);
  useLayoutEffect(() => {
    const element = container.current;
    if (!drawn || !element) {
      return undefined;
    }
    const measure = () => {
      const width = element.clientWidth - 1;
      const height = element.clientHeight - 1;
      setSize((previous) =>
        width <= 0 || height <= 0
          ? null
          : previous && previous.width === width && previous.height === height
            ? previous
            : { width, height },
      );
    };
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [drawn]);

  const layout = useMemo(
    () => (flow ? layoutSupplyFlow(flow, size?.width, size?.height) : null),
    [flow, size],
  );

  // A new response may no longer hold what was selected
  useEffect(() => {
    if (!flow || !selected) {
      return;
    }
    const gone =
      selected.type === "node"
        ? !indexNodes(flow).has(selected.id)
        : selected.index >= flow.bands.length ||
          (!showNeeds && flow.bands[selected.index].need);
    if (gone) {
      setSelected(null);
    }
  }, [flow, selected, showNeeds]);

  const setSettings = useCallback(
    (next: SupplyFlowSettings) => {
      onChange({ settings: { ...state.settings, "supply-flow": next } });
    },
    [onChange, state.settings],
  );

  const setHome = useCallback(
    (next: string | null) => {
      rememberHome(userId, next);
      setSettings({ ...settings, home: next });
    },
    [setSettings, settings, userId],
  );

  const countries = useMemo(
    () =>
      (flow?.columns.find((column) => column.id === "countries")?.nodes ?? [])
        .filter((node) => node.id.startsWith("country:") && node.id !== "country:unknown")
        .map((node) => node.label),
    [flow],
  );

  const needs = flow?.bands.filter((band) => band.need).length ?? 0;
  // In an exported image: the jurisdictions said in words, in place of the
  // controls that set them, and no panel
  const capturing = useCapturing();

  return (
    <Box
      component="section"
      aria-label={ANALYTICS_VIEW_LABELS["supply-flow"]}
      sx={{
        flex: "1 0 auto",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        gap: "14px",
      }}
    >
      {capturing ? (
        (home || settings.trusted.length > 0) && (
          <ExportFacts
            aria-label="Jurisdiction"
            facts={[
              ["Home jurisdiction", home ?? "none"],
              ["Trusted", settings.trusted.join(", ") || "none"],
            ]}
          />
        )
      ) : (
        <SupplyFlowControls
          home={home}
          onHomeChange={setHome}
          trusted={settings.trusted}
          onTrustedChange={(trusted) => setSettings({ ...settings, trusted })}
          countries={countries}
        >
          {needs > 0 && (
            <Chip
              label="needs with nothing on record"
              size="small"
              aria-pressed={showNeeds}
              onClick={() => setShowNeeds((shown) => !shown)}
              sx={
                showNeeds
                  ? {
                      backgroundColor: (theme) => alpha(theme.palette.accent.main, 0.14),
                      borderColor: (theme) => alpha(theme.palette.accent.main, 0.45),
                    }
                  : undefined
              }
            />
          )}
        </SupplyFlowControls>
      )}

      {/* The canvas: as tall as the page leaves, never a strip */}
      <Box
        sx={{
          position: "relative",
          flex: "1 1 0",
          minHeight: 560,
          border: 1,
          borderColor: "divider",
          borderRadius: (theme) => `${theme.radius.surface}px`,
          backgroundColor: "background.paper",
          overflow: "hidden",
        }}
      >
        {!flow || !layout ? (
          <Box sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", p: 3 }}>
            {read.isLoading ? (
              <LogoLoader size="view" surface="paper" label="Loading the supply flow" />
            ) : (
              <Typography color="text.secondary">
                The supply flow could not be loaded.
              </Typography>
            )}
          </Box>
        ) : noKinds ? (
          <Box sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", p: 3 }}>
            <Typography color="text.secondary" align="center">
              No kind of part is on record for the {counted(flow.evidence.n, "product")} of
              this scope, so there is no supply to follow.
            </Typography>
          </Box>
        ) : (
          <>
            <Box ref={container} sx={{ position: "absolute", inset: 0, overflow: "auto" }}>
              <SupplyFlowChart
                flow={flow}
                layout={layout}
                selected={selected}
                hoveredId={hoveredId}
                showNeeds={showNeeds}
                onSelect={setSelected}
                onHover={setHoveredId}
              />
            </Box>
            {selected && !capturing && (
              <SupplyFlowPanel
                flow={flow}
                selected={selected}
                selection={selectionOf(state)}
                onSelect={setSelected}
                onOpenEntity={onOpenEntity}
              />
            )}
          </>
        )}
      </Box>

      {drawn && flow && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Legend flow={flow} showNeeds={showNeeds} />
          <Typography
            variant="caption"
            color="text.disabled"
            component="p"
            sx={{ m: 0 }}
            aria-label="Floors"
          >
            {floorsCaption(flow)}. Counts are distinct products; a product with two
            makers is in two bands, so a kind&apos;s maker bands may sum above its
            count. A host node counts hosts, not parts. A bar too short for its name
            shows it on hover.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SupplyFlowView;
